import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  createSaleUser as createUtilityUser,
  getUtilityUsersDatatable,
  getUtilityUsersLookups,
  getUtilityUserById,
  checkVoucherState,
  updatePaymentMethod,
  updateUtilityUserActivation,
  assignPromoCodeToUtilityUser,
  getUtilityAnalysisDatatable,
  getUtilityDepartmentAnalysis,
  getUtilityPromoCodeAnalysis,
  getUtilityVoucher,
  assignPublisherToUtilityUser
} from './thunks';
import { UtilityUsersState } from './types';

const name = 'utilityUser';

const initialState: UtilityUsersState = {
  utilityUsersDatatable: {
    data: null,
    status: 'idle',
  },
  salesAnalysisDatatable: {
    data: null,
    status: 'idle',
  },
  utilityUsersLookups: {
    data: [],
    status: 'idle',
  },
  utilityUserById: {
    data: undefined,
    status: 'idle',
  },
  createUtilityUser: {
    data: undefined,
    status: 'idle',
  },
  getUtilityDepartmentAnalysis: {
    data: undefined,
    status: 'idle',
  },
  getSalesVoucher: {
    data: undefined,
    status: 'idle',
  },
  checkVoucherState: {
    data: null,
    status: 'idle',
  },
  updatePaymentMethod: {
    status: 'idle',
  },
  updateUtilityUserActivation: {
    status: 'idle',
  },
  assignPromoCodeToUtilityUser: {
    status: 'idle',
  },
  getUtilityPromoCodeAnalysis: {
    data: null,
    status: 'idle',
  },
  assignPublisherToUtilityUser: {
    status: 'idle',
  },
};

export const getUtilityUsersDatatableAsync = createAsyncThunk(
  name + '/getUtilityUsersDatatable',
  getUtilityUsersDatatable
);

export const getUtilityAnalysisDatatableAsync = createAsyncThunk(
  name + '/getUtilityAnalysisDatatable',
  getUtilityAnalysisDatatable
);

export const getUtilityUsersLookupsAsync = createAsyncThunk(
  name + '/getUtilityUsersLookups',
  getUtilityUsersLookups
);

export const getUtilityUserByIdAsync = createAsyncThunk(
  name + '/getUtilityUserById',
  getUtilityUserById
);

export const createUtilityUserAsync = createAsyncThunk(
  name + '/createUtilityUser',
  createUtilityUser
);

export const getUtilityDepartmentAnalysisAsync = createAsyncThunk(
  name + '/getUtilityDepartmentAnalysis',
  getUtilityDepartmentAnalysis
);

export const getUtilityVoucherAsync = createAsyncThunk(
  name + '/getUtilityVoucher',
  getUtilityVoucher
);

export const checkVoucherStateAsync = createAsyncThunk(
  name + '/checkVoucherState',
  checkVoucherState
);

export const updatePaymentMethodAsync = createAsyncThunk(
  name + '/updatePaymentMethod',
  updatePaymentMethod
);

export const updateUtilityUserActivationAsync = createAsyncThunk(
  name + '/updateUtilityUserActivation',
  updateUtilityUserActivation
);

export const assignPromoCodeToUtilityUserAsync = createAsyncThunk(
  name + '/assignPromoCodeToUtilityUser',
  assignPromoCodeToUtilityUser
);

export const assignPublisherToUtilityUserAsync = createAsyncThunk(
  name + '/assignPublisherToUtilityUser',
  assignPublisherToUtilityUser
);

export const getUtilityPromoCodeAnalysisAsync = createAsyncThunk(
  name + '/getUtilityPromoCodeAnalysis',
  getUtilityPromoCodeAnalysis
);

export const slice = createSlice({
  name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUtilityUserAsync.pending, (state) => {
        state.createUtilityUser.status = 'loading';
      })
      .addCase(createUtilityUserAsync.fulfilled, (state) => {
        state.createUtilityUser.status = 'idle';
      })
      .addCase(createUtilityUserAsync.rejected, (state) => {
        state.createUtilityUser.status = 'failed';
      })
      .addCase(getUtilityDepartmentAnalysisAsync.pending, (state) => {
        state.getUtilityDepartmentAnalysis.status = 'loading';
      })
      .addCase(getUtilityDepartmentAnalysisAsync.fulfilled, (state, action) => {
        state.getUtilityDepartmentAnalysis = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getUtilityDepartmentAnalysisAsync.rejected, (state) => {
        state.getUtilityDepartmentAnalysis.status = 'failed';
      })
      .addCase(getUtilityUsersDatatableAsync.pending, (state) => {
        state.utilityUsersDatatable.status = 'loading';
      })
      .addCase(getUtilityUsersDatatableAsync.fulfilled, (state, action) => {
        state.utilityUsersDatatable = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getUtilityUsersDatatableAsync.rejected, (state) => {
        state.utilityUsersDatatable.status = 'failed';
      })
      .addCase(getUtilityUsersLookupsAsync.pending, (state) => {
        state.utilityUsersLookups.status = 'loading';
      })
      .addCase(getUtilityUsersLookupsAsync.fulfilled, (state, action) => {
        state.utilityUsersLookups = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getUtilityUsersLookupsAsync.rejected, (state) => {
        state.utilityUsersLookups.status = 'failed';
      })
      .addCase(getUtilityUserByIdAsync.pending, (state) => {
        state.utilityUserById.status = 'loading';
      })
      .addCase(getUtilityUserByIdAsync.fulfilled, (state, action) => {
        state.utilityUserById = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getUtilityUserByIdAsync.rejected, (state) => {
        state.utilityUserById.status = 'failed';
      })
      .addCase(getUtilityAnalysisDatatableAsync.pending, (state) => {
        state.salesAnalysisDatatable.status = 'loading';
      })
      .addCase(getUtilityAnalysisDatatableAsync.fulfilled, (state, action) => {
        state.salesAnalysisDatatable = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getUtilityAnalysisDatatableAsync.rejected, (state) => {
        state.salesAnalysisDatatable.status = 'failed';
      })
      .addCase(getUtilityVoucherAsync.pending, (state) => {
        state.getSalesVoucher.status = 'loading';
      })
      .addCase(getUtilityVoucherAsync.fulfilled, (state, action) => {
        state.getSalesVoucher = { data: action.payload, status: 'idle' };
      })
      .addCase(getUtilityVoucherAsync.rejected, (state) => {
        state.getSalesVoucher = {
          data: null,
          status: 'idle',
        };
      })
      .addCase(checkVoucherStateAsync.pending, (state) => {
        state.checkVoucherState.status = 'loading';
      })
      .addCase(checkVoucherStateAsync.fulfilled, (state, action) => {
        state.checkVoucherState = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(checkVoucherStateAsync.rejected, (state) => {
        state.checkVoucherState = {
          data: null,
          status: 'idle',
        };
      })
      .addCase(updatePaymentMethodAsync.pending, (state) => {
        state.updatePaymentMethod.status = 'loading';
      })
      .addCase(updatePaymentMethodAsync.fulfilled, (state, action) => {
        state.updatePaymentMethod = {
          status: 'idle',
        };
      })
      .addCase(updatePaymentMethodAsync.rejected, (state) => {
        state.updatePaymentMethod = {
          status: 'idle',
        };
      })
      .addCase(updateUtilityUserActivationAsync.pending, (state) => {
        state.updateUtilityUserActivation.status = 'loading';
      })
      .addCase(updateUtilityUserActivationAsync.fulfilled, (state, action) => {
        state.updateUtilityUserActivation = {
          status: 'idle',
        };
      })
      .addCase(updateUtilityUserActivationAsync.rejected, (state) => {
        state.updateUtilityUserActivation = {
          status: 'idle',
        };
      })
      .addCase(assignPromoCodeToUtilityUserAsync.pending, (state) => {
        state.assignPromoCodeToUtilityUser.status = 'loading';
      })
      .addCase(assignPromoCodeToUtilityUserAsync.fulfilled, (state, action) => {
        state.assignPromoCodeToUtilityUser = {
          status: 'idle',
        };
      })
      .addCase(assignPromoCodeToUtilityUserAsync.rejected, (state) => {
        state.assignPromoCodeToUtilityUser = {
          status: 'idle',
        };
      })
      .addCase(assignPublisherToUtilityUserAsync.pending, (state) => {
        state.assignPublisherToUtilityUser.status = 'loading';
      })
      .addCase(assignPublisherToUtilityUserAsync.fulfilled, (state, action) => {
        state.assignPublisherToUtilityUser = {
          status: 'idle',
        };
      })
      .addCase(assignPublisherToUtilityUserAsync.rejected, (state) => {
        state.assignPublisherToUtilityUser = {
          status: 'idle',
        };
      })
      .addCase(getUtilityPromoCodeAnalysisAsync.pending, (state) => {
        state.getUtilityPromoCodeAnalysis.status = 'loading';
      })
      .addCase(getUtilityPromoCodeAnalysisAsync.fulfilled, (state, action) => {
        state.getUtilityPromoCodeAnalysis = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getUtilityPromoCodeAnalysisAsync.rejected, (state) => {
        state.getUtilityPromoCodeAnalysis.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
